<template>
    <div class="amount-rows">
        <div v-if="!rows.length" class="amount-rows__empty">
            {{ emptyText }}
        </div>
        <v-row v-for="(row, index) in rows" :key="index" dense align="center" class="amount-rows__row">
            <v-col cols="12" :sm="hideAmount ? (readonly ? 12 : 11) : (modes ? 5 : 7)">
                <v-autocomplete
                    :value="row[keyField]"
                    outlined
                    dense
                    hide-details="auto"
                    :items="optionsFor(row)"
                    :item-text="itemText"
                    :item-value="itemValue"
                    :label="selectLabel"
                    :prepend-inner-icon="icon"
                    :readonly="readonly"
                    :rules="[v => !!v || 'Required']"
                    @change="setKey(index, $event)"
                />
            </v-col>
            <v-col v-if="modes" cols="12" sm="3">
                <v-btn-toggle
                    :value="row[modeField]"
                    mandatory
                    dense
                    color="orange darken-2"
                    class="amount-rows__modes"
                    @change="setMode(index, $event)"
                >
                    <v-btn v-for="mode in modes" :key="String(mode.value)" :value="mode.value" small :disabled="readonly">
                        {{ mode.text }}
                    </v-btn>
                </v-btn-toggle>
            </v-col>
            <v-col v-if="!hideAmount" :cols="readonly ? 12 : 10" :sm="readonly ? (modes ? 4 : 5) : (modes ? 3 : 4)">
                <v-text-field
                    :value="row[amountFieldOf(row)]"
                    outlined
                    dense
                    hide-details="auto"
                    type="number"
                    min="0"
                    :max="max"
                    :step="step"
                    :prefix="prefix"
                    :label="amountLabelOf(row)"
                    :readonly="readonly"
                    :rules="[amountRule]"
                    @input="setAmount(index, $event)"
                />
            </v-col>
            <v-col v-if="!readonly" :cols="hideAmount ? 12 : 2" sm="1" class="text-right">
                <v-btn icon small @click="remove(index)">
                    <v-icon small>mdi-close</v-icon>
                </v-btn>
            </v-col>
        </v-row>
        <v-btn v-if="!readonly" small text color="orange darken-2" class="mt-2 px-1" :disabled="!availableCount" @click="add">
            <v-icon left small>mdi-plus</v-icon>{{ addLabel }}
        </v-btn>
    </div>
</template>

<script>
    // Editable list of "pick an item + amount in €" rows (season → payment, arena → cost, staff → payment).
    // Each row is an object holding the picked id in `keyField`, its label in `labelField` and the amount in `amountField`.
    export default {
        name: 'AmountRows',
        props: {
            value: {
                type: Array,
                default: () => []
            },
            items: {
                type: Array,
                default: () => []
            },
            itemText: {
                type: [String, Function],
                default: 'name'
            },
            itemValue: {
                type: String,
                default: 'id'
            },
            keyField: {
                type: String,
                required: true
            },
            labelField: {
                type: String,
                default: null
            },
            amountField: {
                type: String,
                required: true
            },
            selectLabel: {
                type: String,
                default: ''
            },
            amountLabel: {
                type: String,
                default: 'Amount'
            },
            addLabel: {
                type: String,
                default: 'Add'
            },
            emptyText: {
                type: String,
                default: 'Nothing added yet.'
            },
            icon: {
                type: String,
                default: null
            },
            readonly: {
                type: Boolean,
                default: false
            },
            prefix: {
                type: String,
                default: '€'
            },
            step: {
                type: [Number, String],
                default: 0.5
            },
            max: {
                type: Number,
                default: null
            },
            // only the picks are shown (e.g. the payments for users who are not admins)
            hideAmount: {
                type: Boolean,
                default: false
            },
            // optional way of paying per row, e.g. [{ value: false, text: 'Per match', amountField, amountLabel }, ...]:
            // the row keeps the picked value in `modeField` and its amount in that mode's amountField
            modes: {
                type: Array,
                default: null
            },
            modeField: {
                type: String,
                default: 'mode'
            },
            // (id) => amount to fill in when an item is picked on a row whose amount is still empty
            defaultAmount: {
                type: Function,
                default: null
            },
            // the amount may be left empty (e.g. a subscription marked paid without the sum)
            amountOptional: {
                type: Boolean,
                default: false
            }
        },
        computed: {
            rows() {
                return this.value || []
            },
            availableCount() {
                const used = this.rows.map(row => row[this.keyField]).filter(Boolean)
                return this.items.filter(item => !used.includes(item[this.itemValue])).length
            }
        },
        methods: {
            amountRule(v) {
                const empty = v === '' || v === null || v === undefined
                if (empty) return this.amountOptional || (this.max === null ? 'Required' : `0-${this.max}`)
                return (v >= 0 && (this.max === null || v <= this.max)) || (this.max === null ? 'Must be 0 or more' : `0-${this.max}`)
            },
            modeOf(row) {
                return this.modes && (this.modes.find(mode => mode.value === row[this.modeField]) || this.modes[0])
            },
            amountFieldOf(row) {
                const mode = this.modeOf(row)
                return (mode && mode.amountField) || this.amountField
            },
            amountLabelOf(row) {
                const mode = this.modeOf(row)
                return (mode && mode.amountLabel) || this.amountLabel
            },
            setMode(index, value) {
                this.update(index, { [this.modeField]: value })
            },
            // an item can be picked once: other rows' picks are hidden
            optionsFor(row) {
                const used = this.rows.filter(other => other !== row).map(other => other[this.keyField])
                return this.items.filter(item => !used.includes(item[this.itemValue]))
            },
            labelOf(id) {
                const item = this.items.find(option => option[this.itemValue] === id)
                if (!item) return null
                return typeof this.itemText === 'function' ? this.itemText(item) : item[this.itemText]
            },
            update(index, changes) {
                const rows = this.rows.slice()
                rows.splice(index, 1, { ...rows[index], ...changes })
                this.$emit('input', rows)
            },
            setKey(index, id) {
                const changes = { [this.keyField]: id }
                if (this.labelField) changes[this.labelField] = this.labelOf(id)
                const field = this.amountFieldOf(this.rows[index])
                const amount = this.rows[index][field]
                if (this.defaultAmount && (amount === null || amount === '' || amount === undefined)) {
                    const suggested = this.defaultAmount(id)
                    if (suggested !== null && suggested !== undefined) changes[field] = suggested
                }
                this.update(index, changes)
            },
            setAmount(index, amount) {
                this.update(index, { [this.amountFieldOf(this.rows[index])]: amount === '' ? null : Number(amount) })
            },
            add() {
                const row = { [this.keyField]: null, [this.amountField]: null }
                if (this.modes) {
                    row[this.modeField] = this.modes[0].value
                    this.modes.forEach(mode => { if (mode.amountField) row[mode.amountField] = null })
                }
                this.$emit('input', this.rows.concat([row]))
            },
            remove(index) {
                const rows = this.rows.slice()
                rows.splice(index, 1)
                this.$emit('input', rows)
            }
        }
    }
</script>

<style scoped>
.amount-rows__empty {
    color: rgba(0, 0, 0, 0.5);
    font-size: 13px;
    padding: 4px 0 8px;
}

.amount-rows__modes {
    width: 100%;
}

.amount-rows__modes .v-btn {
    flex: 1 1 0;
    height: 40px !important;
}

.amount-rows__row + .amount-rows__row {
    margin-top: 4px;
}
</style>
